import { Link, useRouterState } from '@tanstack/react-router'
import {
  Activity,
  BarChart3,
  Coins,
  FlaskConical,
  FolderGit2,
  MessagesSquare,
  Settings,
  X,
  type LucideIcon,
} from 'lucide-react'

import { env } from '@/config/env'
import { useUnseenExecutions } from '@/features/executions/useUnseenExecutions'
import { cn } from '@/lib/utils/cn'

interface NavItem {
  to: string
  label: string
  hint: string
  icon: LucideIcon
}

/**
 * Grouped the way a console groups them: the things you do, then the things you watch,
 * then configuration. A hairline separates the groups — three destinations do not need a
 * heading to be understood as a set, and on a rail there is no room for one anyway.
 */
const NAV: NavItem[][] = [
  [
    { to: '/chat', label: 'Chat', hint: 'Ask about your repositories', icon: MessagesSquare },
    {
      to: '/repositories',
      label: 'Repositories',
      hint: 'Connected repositories and issues',
      icon: FolderGit2,
    },
  ],
  [
    {
      to: '/executions',
      label: 'Executions',
      hint: 'Every tool call, with outcome and latency',
      icon: Activity,
    },
    { to: '/metrics', label: 'Metrics', hint: 'Live operational metrics', icon: BarChart3 },
    { to: '/benchmark', label: 'Benchmark', hint: 'Tool-selection evaluation', icon: FlaskConical },
  ],
  [
    { to: '/billing', label: 'Credits', hint: 'Balance, usage and top-ups', icon: Coins },
    { to: '/settings', label: 'Settings', hint: 'Integration and tool catalog', icon: Settings },
  ],
]

/**
 * The product mark.
 *
 * A hub with three spokes: an MCP server is the thing standing between one AI client and
 * the systems it reaches, and that is the only idea the mark has to carry. Drawn rather
 * than fetched, so the app has no second origin to depend on for its logo, and geometric
 * enough to stay legible at the 16px of a browser tab — where the same shape is the icon.
 */
export function ProductMark({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" className={className} aria-hidden="true" fill="currentColor">
      <path d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-7-.5-14.5T799-507q-5 29-27 48t-52 19h-80q-33 0-56.5-23.5T560-520v-40H400v-80q0-33 23.5-56.5T480-720h40q0-23 12.5-40.5T563-789q-20-5-40.5-8t-42.5-3q-134 0-227 93t-93 227h200q66 0 113 47t47 113v40H400v110q20 5 39.5 7.5T480-160Z"/>
    </svg>
  )
}

/**
 * The navigation rail.
 *
 * Icons only, the width of one target, always in place — the shape messaging apps settled
 * on and everyone can already read. Six destinations are few enough to recognise by icon,
 * and the hours people spend here are spent in the content beside it, not in the nav.
 *
 * Nothing is hidden from anyone who cannot see the icons: each destination keeps its name
 * in the accessibility tree, and a hover tooltip says what it is for.
 */
export function Sidebar({ onNavigate, isMobile }: { onNavigate?: () => void; isMobile?: boolean }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const groups = NAV.map((group) =>
    group.filter((item) => item.to !== '/chat' || env.chatEnabled),
  ).filter((group) => group.length > 0)
  // Tool calls this user has not looked at yet. Not a total: the page holds the whole
  // history, and a number that only ever grows stops meaning anything.
  const { data: unseen } = useUnseenExecutions()
  const newActivity = unseen?.count ?? 0

  return (
    <nav
      className={cn(
        'flex h-full flex-col',
        isMobile ? 'w-[320px] max-w-[85vw]' : 'w-16 items-center gap-1 py-3'
      )}
      aria-label="Main"
    >
      {isMobile ? (
        <div className="flex h-14 shrink-0 items-center justify-between px-4 border-b border-line">
          <Link
            to="/executions"
            onClick={onNavigate}
            title="RepoPilot"
            className="flex h-10 w-10 items-center justify-center rounded-lg text-ink"
          >
            <ProductMark className="h-7 w-7" />
          </Link>
          <button
            type="button"
            className="rounded-md p-2 text-ink-muted hover:bg-hover hover:text-ink"
            onClick={onNavigate}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ) : (
        <Link
          to="/executions"
          onClick={onNavigate}
          title="RepoPilot · GitHub DevOps"
          className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-ink"
        >
          <ProductMark className="h-8 w-8" />
          <span className="sr-only">RepoPilot</span>
        </Link>
      )}

      <div className={cn(isMobile && 'flex flex-col overflow-y-auto px-3 py-4')}>
        {groups.map((group, index) => (
          <div
            key={index}
            className={cn(
              'flex flex-col gap-1',
              isMobile
                ? index > 0 && 'mt-2 border-t border-line pt-4'
                : 'w-full items-center',
              !isMobile && index > 0 && 'mt-1 border-t border-line pt-2'
            )}
          >
            {group.map((item) => {
              const active = pathname === item.to || pathname.startsWith(`${item.to}/`)
              const Icon = item.icon
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  title={`${item.label} — ${item.hint}`}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'transition-colors duration-100',
                    isMobile
                      ? cn(
                          'flex items-center gap-3 rounded-md px-3 py-1.5 text-xs font-medium',
                          active
                            ? 'bg-active text-ink'
                            : 'text-ink-muted hover:bg-hover hover:text-ink'
                        )
                      : cn(
                          'relative flex h-11 w-11 items-center justify-center rounded-full',
                          active
                            ? 'bg-active text-ink'
                            : 'text-ink-muted hover:bg-hover hover:text-ink'
                        )
                  )}
                >
                  <Icon aria-hidden="true" className={isMobile ? 'h-3.5 w-3.5' : 'h-[22px] w-[22px]'} />
                  {isMobile ? <span>{item.label}</span> : <span className="sr-only">{item.label}</span>}
                  {item.to === '/executions' && newActivity > 0 ? (
                    <span
                      aria-label={`${newActivity} new execution${newActivity === 1 ? '' : 's'}`}
                      className={cn(
                        'rounded-full bg-primary font-semibold text-ground',
                        isMobile
                          ? 'ml-auto px-1.5 py-0.5 text-[10px]'
                          : 'absolute -right-0.5 -top-0.5 min-w-[1.125rem] px-1 text-center text-[10px] leading-[1.125rem] ring-2 ring-surface'
                      )}
                    >
                      {newActivity > 99 ? '99+' : newActivity}
                    </span>
                  ) : null}
                </Link>
              )
            })}
          </div>
        ))}
      </div>
    </nav>
  )
}
